import { useEffect, useState } from "react";
import { ArrowLeftRight, Loader2, AlertTriangle, Check } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

// Convert Person <-> Organization (Lot C3). Advanced-management zone, distinct from Delete.
export function PartyConvertZone({ stableId, partyType, partyId, name, onConverted }) {
  const base = partyType === "organization" ? "organizations" : "persons";
  const isPerson = partyType !== "organization";
  const targetVerb = isPerson ? "Converti in organizzazione" : "Converti in persona";
  const targetLabel = isPerson ? "Organizzazione" : "Persona";
  const currentLabel = isPerson ? "Persona" : "Organizzazione";
  const [elig, setElig] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!stableId || !partyId) return;
    let alive = true;
    setElig(null);
    api.get(`/stables/${stableId}/${base}/${partyId}/convert-eligibility`)
      .then((r) => { if (alive) setElig(r.data); })
      .catch(() => { if (alive) setElig({ can_convert: false, blockers: [], error: true }); });
    return () => { alive = false; };
  }, [stableId, base, partyId]);

  const doConvert = async () => {
    setBusy(true);
    try {
      const to = isPerson ? "convert-to-organization" : "convert-to-person";
      const { data } = await api.post(`/stables/${stableId}/${base}/${partyId}/${to}`);
      toast.success(isPerson ? "Convertita in organizzazione" : "Convertita in persona");
      setOpen(false);
      const newId = data.organization_id || data.person_id;
      onConverted && onConverted(isPerson ? "organization" : "person", newId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d?.reason || "Conversione non possibile") : (d || "Errore"));
      if (typeof d === "object") setElig({ ...d, can_convert: false });
      setOpen(false);
    } finally { setBusy(false); }
  };

  if (elig === null || elig.error) return null;
  const notTransferable = (elig.mapping?.not_transferable) || [];
  const preserved = (elig.mapping?.preserved) || [];

  return (
    <div className="mt-3" data-testid="party-convert-zone">
      {elig.can_convert ? (
        <button data-testid="party-convert-open" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm text-[#8C532B] hover:text-[#5F4626]">
          <ArrowLeftRight className="h-4 w-4" /> {targetVerb}
        </button>
      ) : (
        <div className="rounded-lg bg-[#FBF6EE] border border-[#E5DEC9] p-3" data-testid="party-convert-blocked">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[#8C6D4F]" />
            <div className="min-w-0">
              <p className="text-sm text-[#5F4626]">{elig.reason || `Questa ${currentLabel.toLowerCase()} non può essere convertita.`}</p>
              {(elig.blockers || []).length > 0 && (
                <ul className="mt-1.5 space-y-0.5" data-testid="party-convert-blockers">
                  {elig.blockers.map((b, i) => (
                    <li key={i} className="text-xs text-[#8C6D4F] flex items-center gap-1.5">
                      <span className="h-1 w-1 rounded-full bg-[#8C6D4F]" /> {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent data-testid="party-convert-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{targetVerb}?</DialogTitle>
            <DialogDescription>
              «{name}» passerà da <strong>{currentLabel}</strong> a <strong>{targetLabel}</strong>. L'identità di origine viene archiviata (non eliminata) e resta ripristinabile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {preserved.length > 0 && (
              <div data-testid="party-convert-preserved">
                <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#9AA39C] mb-1">Verrà mantenuto</p>
                <ul className="space-y-0.5">
                  {preserved.map((p, i) => (
                    <li key={i} className="flex items-center gap-1.5 text-[#1B3B2B]"><Check className="h-3.5 w-3.5 text-[#2E7D5B]" /> {p}</li>
                  ))}
                </ul>
              </div>
            )}
            {notTransferable.length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid="party-convert-nottransferable">
                <p className="text-xs text-amber-800 font-medium mb-1">Dati non trasferibili (verranno persi):</p>
                <ul className="space-y-0.5">
                  {notTransferable.map((p, i) => <li key={i} className="text-xs text-amber-800">• {p}</li>)}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="party-convert-cancel">Annulla</Button>
            <Button className="bg-[#8C532B] hover:bg-[#6f4021] text-white" onClick={doConvert} disabled={busy} data-testid="party-convert-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {targetVerb}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
